/**
 * EntityProfile — View entity details with assets and relationships.
 * Mirrors Tauri EntityProfileBeautiful for feature parity.
 */
import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  Music,
  Building2,
  Users,
  User,
  Mail,
  Phone,
  Globe,
  Instagram,
  Twitter,
  Facebook,
  Image as ImageIcon,
  Grid,
  List,
  Link2,
} from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';

interface EntityProfileProps {
  entity: {
    _id: Id<'entities'>;
    name: string;
    displayName?: string;
    description?: string;
    entityType: string;
    bio?: string;
    contactInfo?: {
      email?: string;
      phone?: string;
      website?: string;
    };
    socialLinks?: {
      instagram?: string;
      twitter?: string;
      facebook?: string;
    };
    brandColors?: string[];
  };
  onBack: () => void;
  onEdit?: () => void;
}

export function EntityProfile({ entity, onBack, onEdit }: EntityProfileProps) {
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const [activeTab, setActiveTab] = useState<'assets' | 'related' | 'details'>('assets');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  const assets = useQuery(api.assets.list, { 
    accountId,
  });
  
  const entityAssets = assets?.filter((a) => a.ownerEntityId === entity._id) || [];
  
  const typeMeta = getEntityTypeMeta(entity.entityType);
  const TypeIcon = typeMeta.icon;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border">
        <div className="h-48 bg-gradient-to-br from-primary/20 via-primary/10 to-background relative">
          <button
            onClick={onBack}
            className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background/80 hover:bg-background text-sm font-medium transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          
          {onEdit && (
            <button
              onClick={onEdit}
              className="absolute top-4 right-4 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Edit
            </button>
          )}

          <div className="absolute -bottom-10 left-6 flex items-end gap-4">
            <div className="h-20 w-20 rounded-2xl bg-card border-4 border-background flex items-center justify-center shadow-lg">
              <TypeIcon className="h-10 w-10 text-primary" />
            </div>
            <div className="mb-11">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                {typeMeta.label}
              </span>
            </div>
          </div>
        </div>

        <div className="pt-12 pb-4 px-6">
          <h1 className="text-2xl font-bold">{entity.displayName || entity.name}</h1>
          {entity.displayName && (
            <p className="text-muted-foreground text-sm">{entity.name}</p>
          )}
          {entity.description && (
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              {entity.description}
            </p>
          )}

          {/* Stats */}
          <div className="flex gap-6 mt-4">
            <div className="text-center">
              <div className="text-xl font-bold">{entityAssets.length}</div>
              <div className="text-xs text-muted-foreground">Assets</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold">0</div>
              <div className="text-xs text-muted-foreground">Related</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border px-6">
        {(['assets', 'related', 'details'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              "px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors",
              activeTab === tab
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'assets' && (
          <div>
            {/* View Toggle */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">Assets ({entityAssets.length})</h3>
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    "p-1.5 rounded transition-colors",
                    viewMode === 'grid' ? "bg-background shadow-sm" : "hover:bg-background/50"
                  )}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    "p-1.5 rounded transition-colors",
                    viewMode === 'list' ? "bg-background shadow-sm" : "hover:bg-background/50"
                  )}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>

            {entityAssets.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground">No assets yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload assets for this entity
                </p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {entityAssets.map((asset) => (
                  <div
                    key={asset._id}
                    className="group relative rounded-xl border border-border bg-card overflow-hidden"
                  >
                    <div className="aspect-square bg-muted flex items-center justify-center">
                      <img
                        src={asset.url}
                        alt={asset.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3">
                      <p className="font-medium text-sm truncate">{asset.name}</p>
                      <p className="text-xs text-muted-foreground">{asset.type}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {entityAssets.map((asset) => (
                  <div
                    key={asset._id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card"
                  >
                    <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                      <img
                        src={asset.url}
                        alt={asset.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{asset.name}</p>
                      <p className="text-xs text-muted-foreground">{asset.type}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'related' && (
          <div className="text-center py-12">
            <Link2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No related entities</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create relationships to see connected entities
            </p>
          </div>
        )}

        {activeTab === 'details' && (
          <div className="space-y-6 max-w-2xl">
            {entity.bio && (
              <div>
                <h3 className="font-medium mb-2">Bio</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {entity.bio}
                </p>
              </div>
            )}

            {/* Contact Info */}
            {(entity.contactInfo?.email || entity.contactInfo?.phone || entity.contactInfo?.website) && (
              <div>
                <h3 className="font-medium mb-2">Contact</h3>
                <div className="space-y-2">
                  {entity.contactInfo.email && (
                    <a
                      href={`mailto:${entity.contactInfo.email}`}
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Mail className="h-4 w-4" />
                      {entity.contactInfo.email}
                    </a>
                  )}
                  {entity.contactInfo.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      {entity.contactInfo.phone}
                    </div>
                  )}
                  {entity.contactInfo.website && (
                    <a
                      href={entity.contactInfo.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Globe className="h-4 w-4" />
                      {entity.contactInfo.website}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Social Links */}
            {(entity.socialLinks?.instagram || entity.socialLinks?.twitter || entity.socialLinks?.facebook) && (
              <div>
                <h3 className="font-medium mb-2">Social</h3>
                <div className="flex flex-wrap gap-2">
                  {entity.socialLinks.instagram && (
                    <a
                      href={`https://instagram.com/${entity.socialLinks.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-sm hover:bg-muted/80 transition-colors"
                    >
                      <Instagram className="h-4 w-4" />
                      {entity.socialLinks.instagram}
                    </a>
                  )}
                  {entity.socialLinks.twitter && (
                    <a
                      href={`https://twitter.com/${entity.socialLinks.twitter.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-sm hover:bg-muted/80 transition-colors"
                    >
                      <Twitter className="h-4 w-4" />
                      {entity.socialLinks.twitter}
                    </a>
                  )}
                  {entity.socialLinks.facebook && (
                    <a
                      href={entity.socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-sm hover:bg-muted/80 transition-colors"
                    >
                      <Facebook className="h-4 w-4" />
                      Facebook
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Brand Colors */}
            {entity.brandColors && entity.brandColors.length > 0 && (
              <div>
                <h3 className="font-medium mb-2">Brand Colors</h3>
                <div className="flex flex-wrap gap-2">
                  {entity.brandColors.map((color, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card"
                    >
                      <div
                        className="h-4 w-4 rounded-full border border-border"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm font-mono">{color}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function getEntityTypeMeta(type: string) {
  switch (type) {
    case 'venue':
      return { label: 'Venue', icon: Building2 };
    case 'artist':
      return { label: 'Artist', icon: Music };
    case 'promoter':
      return { label: 'Promoter', icon: Users };
    default:
      return { label: 'Entity', icon: User };
  }
}
